import { type Static, Type } from "@sinclair/typebox"
import { DOMAIN_SLUG_REGEX, USERNAME_REGEX } from "../identifier"
import { SecondaryDomainSchema, SessionUserSchema } from "./auth"
import {
  CursorQuerySchema,
  createPageSchema,
  IsoDateTimeSchema,
  JsonRecordSchema,
  Nullable,
  ShardInfoSchema,
} from "./common"

// Administración de usuarios — docs/04 §3.

export const UserStatusSchema = Type.Union([
  Type.Literal("active"),
  Type.Literal("locked"),
  Type.Literal("suspended"),
  Type.Literal("deleted"),
])
export type UserStatus = Static<typeof UserStatusSchema>

export const AdminUserSchema = Type.Object({
  id: Type.String(),
  domain_id: Type.String(),
  // Slug del dominio, para listados sin join en el cliente
  domain: Type.String(),
  username: Type.String(),
  identifier: Type.String(),
  email: Nullable(Type.String({ format: "email" })),
  name: Nullable(Type.String()),
  profile: JsonRecordSchema,
  status: UserStatusSchema,
  is_master: Type.Boolean(),
  // Acceso RESTRINGIDO: solo ve los vehículos/grupos/zonas/personal asignados.
  restricted: Type.Boolean(),
  // Su acceso por vehículo le da TODA la flota aunque esté restringido (zonas y
  // personal siguen limitados). Sin efecto si `restricted` está apagado.
  all_vehicles: Type.Boolean(),
  // Nombres de los roles asignados
  roles: Type.Array(Type.String()),
  mfa_enabled: Type.Boolean(),
  locked_until: Nullable(IsoDateTimeSchema),
  last_login_at: Nullable(IsoDateTimeSchema),
  created_by: Nullable(Type.String()),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
})
export type AdminUser = Static<typeof AdminUserSchema>

// GET /admin/users?domain=&q=&status=&cursor=
export const ListUsersQuerySchema = Type.Composite([
  CursorQuerySchema,
  Type.Object({
    domain: Type.Optional(Type.String()),
    q: Type.Optional(Type.String()),
    status: Type.Optional(UserStatusSchema),
  }),
])
export type ListUsersQuery = Static<typeof ListUsersQuerySchema>

export const AdminUserPageSchema = createPageSchema(AdminUserSchema)
export type AdminUserPage = Static<typeof AdminUserPageSchema>

// Sin password ⇒ alta por invitación (enlace de un solo uso) [V1].
export const CreateUserRequestSchema = Type.Object({
  domain_id: Type.String(),
  username: Type.String({ pattern: USERNAME_REGEX.source }),
  password: Type.Optional(Type.String({ minLength: 1 })),
  email: Type.Optional(Type.String({ format: "email" })),
  name: Type.Optional(Type.String()),
  role_ids: Type.Optional(Type.Array(Type.String())),
  send_invite: Type.Optional(Type.Boolean()),
})
export type CreateUserRequest = Static<typeof CreateUserRequestSchema>

// El admin/maestro sí puede cambiar el username (a diferencia de /v1/me).
export const UpdateUserRequestSchema = Type.Object({
  username: Type.Optional(Type.String({ pattern: USERNAME_REGEX.source })),
  email: Type.Optional(Nullable(Type.String({ format: "email" }))),
  name: Type.Optional(Nullable(Type.String())),
  profile: Type.Optional(JsonRecordSchema),
  status: Type.Optional(Type.Union([Type.Literal("active"), Type.Literal("suspended")])),
  // Marca de acceso restringido. Se rechaza sobre el maestro del dominio y sobre
  // usuarios de la instancia (`system`): nadie puede dejar sin acceso al dueño.
  restricted: Type.Optional(Type.Boolean()),
  // TODOS los vehículos aunque esté restringido: lo fija la configuración de acceso
  // por vehículo («Acceso a dispositivos» → todos), que gana sobre la marca.
  all_vehicles: Type.Optional(Type.Boolean()),
})
export type UpdateUserRequest = Static<typeof UpdateUserRequestSchema>

// GET/PUT /admin/users/{id}/scopes — scopes DIRECTOS del usuario, además de los de
// sus roles. Solo suman. El PUT reemplaza el conjunto completo; el que concede no
// puede dar un scope que no tenga (misma anti-escalada que los roles).
export const ReplaceUserScopesRequestSchema = Type.Object({
  scope_ids: Type.Array(Type.String(), { maxItems: 200 }),
})
export type ReplaceUserScopesRequest = Static<typeof ReplaceUserScopesRequestSchema>

export const UserScopeEntrySchema = Type.Object({
  id: Type.String(),
  key: Type.String(),
  description: Nullable(Type.String()),
})
export type UserScopeEntry = Static<typeof UserScopeEntrySchema>

export const UserScopesResponseSchema = Type.Object({
  data: Type.Array(UserScopeEntrySchema),
})
export type UserScopesResponse = Static<typeof UserScopesResponseSchema>

// GET/PUT /admin/users/{id}/vehicle-access — VEHÍCULOS y GRUPOS asignados a un
// usuario restringido. Los ids son de auth (`veh_…`, `vehgrp_…`). El PUT reemplaza
// el conjunto completo. Pertenecer a un grupo asignado da acceso a sus vehículos.
export const ReplaceUserVehicleAccessRequestSchema = Type.Object({
  vehicle_ids: Type.Array(Type.String(), { maxItems: 5000 }),
  group_ids: Type.Array(Type.String(), { maxItems: 500 }),
})
export type ReplaceUserVehicleAccessRequest = Static<typeof ReplaceUserVehicleAccessRequestSchema>

// POST /admin/users/{id}/impersonate — sesión «Ver como»: access token del usuario
// objetivo (TTL corto, SIN refresh) con el claim `imp` del administrador. Mismo
// cuerpo que un login para que el app arranque igual, más quién impersona.
export const ImpersonateResponseSchema = Type.Object({
  token_type: Type.Literal("Bearer"),
  access_token: Type.String(),
  expires_in: Type.Integer(),
  user: SessionUserSchema,
  shard: Nullable(ShardInfoSchema),
  impersonated_by: Type.Object({ id: Type.String(), username: Type.String() }),
})
export type ImpersonateResponse = Static<typeof ImpersonateResponseSchema>

export const UserVehicleAccessResponseSchema = Type.Object({
  user_id: Type.String(),
  restricted: Type.Boolean(),
  // true = ve TODA la flota aunque esté restringido: estas listas no lo acotan.
  all_vehicles: Type.Boolean(),
  vehicle_ids: Type.Array(Type.String()),
  group_ids: Type.Array(Type.String()),
})
export type UserVehicleAccessResponse = Static<typeof UserVehicleAccessResponseSchema>

// PUT /admin/users/{id}/roles — reemplaza el conjunto completo.
export const ReplaceUserRolesRequestSchema = Type.Object({
  role_ids: Type.Array(Type.String()),
})
export type ReplaceUserRolesRequest = Static<typeof ReplaceUserRolesRequestSchema>

// GET/PUT /admin/users/{id}/domain-access — tenants SECUNDARIOS del usuario:
// dominios que puede mirar además del suyo. Habilitan el selector de tenant sin
// darle `gw:tenants:all` (god-view sobre TODOS los clientes, no acotable). El PUT
// reemplaza el conjunto completo; una lista vacía se los quita todos.
export const ReplaceUserDomainAccessRequestSchema = Type.Object({
  domains: Type.Array(Type.String({ pattern: DOMAIN_SLUG_REGEX.source })),
})
export type ReplaceUserDomainAccessRequest = Static<typeof ReplaceUserDomainAccessRequestSchema>

export const UserDomainAccessResponseSchema = Type.Object({
  // El dominio PROPIO del usuario, con su nodo. Va aquí porque el consumidor —el
  // panel del gateway— necesita el mapa `tenant → nodo` de TODOS sus tenants
  // para repartir el acceso por vehículo entre nodos, y la vía normal para ese
  // mapa (`/v1/admin/domains`) exige ser maestro.
  home: SecondaryDomainSchema,
  data: Type.Array(SecondaryDomainSchema),
})

// PUT /admin/users/{id}/password — el administrador FIJA la contraseña (además del
// enlace de reset). La política (largo mínimo/máximo) la aplica el servicio.
export const AdminSetPasswordRequestSchema = Type.Object({
  password: Type.String({ minLength: 1, maxLength: 512 }),
})
export type AdminSetPasswordRequest = Static<typeof AdminSetPasswordRequestSchema>

// POST /admin/users/{id}/reset-password — genera enlace de un solo uso.
export const AdminResetPasswordResponseSchema = Type.Object({
  reset_url: Type.String(),
  expires_at: IsoDateTimeSchema,
})
export type AdminResetPasswordResponse = Static<typeof AdminResetPasswordResponseSchema>

// Sesiones activas [V1] — una entrada por familia de refresh tokens.
export const UserSessionSchema = Type.Object({
  family_id: Type.String(),
  client: Type.Union([Type.Literal("app"), Type.Literal("console")]),
  ip: Nullable(Type.String()),
  user_agent: Nullable(Type.String()),
  created_at: IsoDateTimeSchema,
  last_used_at: Nullable(IsoDateTimeSchema),
  expires_at: IsoDateTimeSchema,
})
export type UserSession = Static<typeof UserSessionSchema>

export const UserSessionsResponseSchema = Type.Object({
  data: Type.Array(UserSessionSchema),
})
export type UserSessionsResponse = Static<typeof UserSessionsResponseSchema>

// POST /admin/users/{id}/shard — reasignación manual; responde ShardResolveResponse.
export const AssignShardRequestSchema = Type.Object({
  node_id: Type.String(),
})
export type AssignShardRequest = Static<typeof AssignShardRequestSchema>
